import { create } from 'zustand';
import type { EncryptedWalletBlob, WatchedAddress } from '@gonka-wallet/storage';

export type ActiveEntry =
  | { readonly kind: 'full'; readonly wallet: EncryptedWalletBlob }
  | { readonly kind: 'watch'; readonly watched: WatchedAddress };

interface WalletState {
  wallets: EncryptedWalletBlob[];
  watched: WatchedAddress[];
  activeId: string | null;
  setWallets: (wallets: EncryptedWalletBlob[]) => void;
  addWallet: (wallet: EncryptedWalletBlob) => void;
  removeWallet: (id: string) => void;
  setWatched: (watched: WatchedAddress[]) => void;
  addWatched: (watched: WatchedAddress) => void;
  removeWatched: (id: string) => void;
  setActive: (id: string | null) => void;
}

function pickFallbackActive(
  wallets: EncryptedWalletBlob[],
  watched: WatchedAddress[],
): string | null {
  return wallets[0]?.id ?? watched[0]?.id ?? null;
}

export const useWalletStore = create<WalletState>((set) => ({
  wallets: [],
  watched: [],
  activeId: null,
  setWallets: (wallets) =>
    set((state) => ({
      wallets,
      activeId: state.activeId ?? wallets[0]?.id ?? state.watched[0]?.id ?? null,
    })),
  addWallet: (wallet) =>
    set((state) => ({
      wallets: [...state.wallets, wallet],
      activeId: state.activeId ?? wallet.id,
    })),
  removeWallet: (id) =>
    set((state) => {
      const wallets = state.wallets.filter((w) => w.id !== id);
      return {
        wallets,
        activeId:
          state.activeId === id
            ? pickFallbackActive(wallets, state.watched)
            : state.activeId,
      };
    }),
  setWatched: (watched) =>
    set((state) => ({
      watched,
      activeId: state.activeId ?? state.wallets[0]?.id ?? watched[0]?.id ?? null,
    })),
  addWatched: (w) =>
    set((state) => ({
      watched: [...state.watched, w],
      activeId: state.activeId ?? w.id,
    })),
  removeWatched: (id) =>
    set((state) => {
      const watched = state.watched.filter((w) => w.id !== id);
      return {
        watched,
        activeId:
          state.activeId === id
            ? pickFallbackActive(state.wallets, watched)
            : state.activeId,
      };
    }),
  setActive: (id) => set({ activeId: id }),
}));

/**
 * Look up the currently active entry across both full wallets and
 * watched addresses. Returns a discriminated union so callers can
 * safely branch on `.kind` without juggling undefined.
 */
export function useActiveEntry(): ActiveEntry | null {
  const wallets = useWalletStore((s) => s.wallets);
  const watched = useWalletStore((s) => s.watched);
  const activeId = useWalletStore((s) => s.activeId);
  if (activeId === null) return null;
  const full = wallets.find((w) => w.id === activeId);
  if (full) return { kind: 'full', wallet: full };
  const w = watched.find((wa) => wa.id === activeId);
  if (w) return { kind: 'watch', watched: w };
  return null;
}
